/* CookieConsentProvider — tenant-scoped consent context. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import {
  customerConsentBus,
  publishCustomerConsentUpdated,
} from '../../shared/crossTab/customerCrossTab.js';
import {
  buildAcceptAll,
  buildAcceptNecessary,
  buildAcceptSelection,
  type ConsentDecision,
} from './consentTypes.js';
import { writeConsent, clearConsent } from './consentStorage.js';
import { buildConsentPreferencesPayload } from './consentPreferencesPayload.js';
import {
  readRevokeContext,
  syncAnalyticsConsentRevoke,
} from './consentRevokeSync.js';
import { customerLogger } from '../../shared/logging/customerLogger.js';
import { loadRuntimeConfig } from '../../config/runtimeConfig.js';
import { endCustomerSessionGlobal } from '../../shared/session/customerAuthSync.js';
import { useLoyaltyProfilingAvailable } from './useLoyaltyProfilingAvailable.js';
import { readEffectiveConsent, GLOBAL_ACCEPT_ALL_KEY } from './consentPolicy.js';
import { CONSENT_VERSION } from '../../shared/consent/constants.js';
import { notifyConsentSyncFailed } from './consentSyncNotice.js';
import { resolveConsentStorageTenantCode, PLATFORM_CONSENT_STORAGE_TENANT } from './consentStorageTenant.js';
import { putPlatformConsentPreferences } from './platformConsentApi.js';
import { resolveConsentApiFallbackTenantCode } from './resolveConsentApiFallbackTenantCode.js';
import { usePlatformAccountMemberships } from '../account/accountApi.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { syncLoyaltyProfilingConsent } from './syncLoyaltyProfilingConsent.js';
import { shouldHandleConsentCrossTabMessage } from './consentCrossTabMessage.js';

interface CookieConsentContextValue {
  decision: ConsentDecision | null;
  /** False until storage has been read (after loyalty profiling availability resolves). */
  isConsentHydrated: boolean;
  isBannerOpen: boolean;
  bannerPreselectFunctional: boolean;
  openBanner: (opts?: { preselectFunctional?: boolean; force?: boolean }) => void;
  closeBanner: () => void;
  acceptAll: () => void;
  acceptNecessary: () => void;
  acceptSelection: (selection: {
    functional: boolean;
    analytics: boolean;
    marketing: boolean;
    profiling?: boolean;
  }) => void;
  /**
   * Grant Functional for “remember me” without dismissing the CMP banner.
   * Preserves analytics / marketing / profiling as already chosen (or false).
   */
  enableLoginPersistence: () => void;
  loyaltyProfilingAvailable: boolean;
  profilingLoading: boolean;
  isLoggedIn: boolean;
  loginPersistenceAllowed: boolean;
  /**
   * Show auth “remember me” when Functional was not already ON at CMP hydrate.
   * Stays true for this provider mount even if remember-me later grants Functional.
   */
  showRememberMeField: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(
  null
);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export function CookieConsentProvider({
  children,
}: CookieConsentProviderProps): JSX.Element {
  const tenantCode = useTenantCode();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { session, sessionHydrated } = useCustomerSession();
  const { loyaltyProfilingAvailable, isLoading: profilingLoading } = useLoyaltyProfilingAvailable();
  const platformMemberships = usePlatformAccountMemberships(
    isPlatformChromeTenant(tenantCode) && sessionHydrated && session !== null,
  );
  const isLoggedIn = sessionHydrated && session !== null;
  const sessionCustomerId = session?.customerId ?? null;
  const [decision, setDecision] = useState<ConsentDecision | null>(null);
  /**
   * GDPR CMP timing (Cookiebot / OneTrust-class):
   * - Stay closed until localStorage has been evaluated.
   * - Never flash open for returning visitors who already decided.
   * - Open only when effective consent is missing (first visit / expired / version bump).
   */
  const [consentHydrated, setConsentHydrated] = useState(false);
  /** Locked on first storage hydrate — remember-me UI visibility must not chase later grants. */
  const [functionalAtHydration, setFunctionalAtHydration] = useState<boolean | null>(
    null
  );
  const [bannerOpen, setBannerOpen] = useState(false);
  const [bannerPreselectFunctional, setBannerPreselectFunctional] = useState(false);
  const prevAnalyticsRef = useRef<boolean | null>(null);
  /** Prefer-center / edit-consent reopen — do not let storage rehydrate close the CMP. */
  const manualBannerOpenRef = useRef(false);

  const applyEffectiveConsentFromStorage = useCallback((): void => {
    const next = readEffectiveConsent({
      tenantCode,
      consentVersion: CONSENT_VERSION,
      loyaltyProfilingAvailable,
      storage: typeof window === 'undefined' ? null : window.localStorage,
    });
    const prevAnalytics = prevAnalyticsRef.current;
    prevAnalyticsRef.current = next?.analytics ?? null;
    if (
      prevAnalytics === true &&
      next !== null &&
      next.analytics === false
    ) {
      const ctx = readRevokeContext(tenantCode);
      void syncAnalyticsConsentRevoke({
        tenantCode,
        api,
        decision: next,
        anonymousKey: ctx.anonymousKey,
        sessionIds: ctx.sessionIds,
        authToken: null,
        signedIn: isLoggedIn,
        skipPreferencesPut: true,
        memberships: platformMemberships.data?.memberships,
      });
    }
    setDecision(next);
    if (!manualBannerOpenRef.current) {
      setBannerOpen(next === null);
    }
    setConsentHydrated(true);
    setFunctionalAtHydration((prev) =>
      prev !== null ? prev : next !== null && next.functional === true
    );
  }, [api, isLoggedIn, loyaltyProfilingAvailable, platformMemberships.data, tenantCode]);

  useEffect(() => {
    if (profilingLoading) {
      // Hold the CMP closed while loyalty availability is unknown — opening
      // here would flash the banner for returning logged-in users.
      return;
    }
    window.queueMicrotask(() => {
      applyEffectiveConsentFromStorage();
    });
  }, [applyEffectiveConsentFromStorage, profilingLoading]);

  useEffect(() => {
    const unsubscribe = customerConsentBus.subscribe((message) => {
      if (message.type !== 'consent-updated') {
        return;
      }
      if (
        !shouldHandleConsentCrossTabMessage(
          message,
          tenantCode,
          null,
        )
      ) {
        return;
      }
      if (message.source === 'admin') {
        void queryClient.invalidateQueries({
          queryKey: customerQueryKeys.marketingAnalyticsConsentPrompt(
            PLATFORM_CONSENT_STORAGE_TENANT,
          ),
        });
      }
      if (message.scope === 'global') {
        // Re-hydrate from global accept-all only when that key exists; partial
        // platform writes remove global and update current directly (G9 / T-E08).
        if (typeof window !== 'undefined' && window.localStorage.getItem(GLOBAL_ACCEPT_ALL_KEY) !== null) {
          clearConsent(tenantCode);
        }
      }
      applyEffectiveConsentFromStorage();
    });
    return unsubscribe;
  }, [
    applyEffectiveConsentFromStorage,
    queryClient,
    tenantCode,
  ]);

  const persist = useCallback(
    (next: ConsentDecision): void => {
      const revokeContext = next.analytics ? null : readRevokeContext(tenantCode);
      writeConsent(tenantCode, next, undefined, { loyaltyProfilingAvailable });
      setDecision(next);
      manualBannerOpenRef.current = false;
      setBannerOpen(false);
      setBannerPreselectFunctional(false);
      prevAnalyticsRef.current = next.analytics;

      void (async (): Promise<void> => {
        if (!next.analytics && revokeContext !== null) {
          await syncAnalyticsConsentRevoke({
            tenantCode,
            api,
            decision: next,
            anonymousKey: revokeContext.anonymousKey,
            sessionIds: revokeContext.sessionIds,
            authToken: null,
            // G6 Spec A: cookie XOR via ApiClient when signed in.
            signedIn: isLoggedIn,
            memberships: platformMemberships.data?.memberships,
          });
        } else if (isLoggedIn && next.analytics) {
          try {
            const storageTenant = resolveConsentStorageTenantCode(tenantCode);
            const { apiBaseUrl } = loadRuntimeConfig();
            await putPlatformConsentPreferences({
              apiBaseUrl,
              body: buildConsentPreferencesPayload(
                storageTenant,
                {
                  analytics: next.analytics,
                  marketing: next.marketing,
                },
              ),
              rollout: {
                api,
                fallbackTenantCode: resolveConsentApiFallbackTenantCode(
                  tenantCode,
                  platformMemberships.data?.memberships,
                ),
              },
            });
          } catch (err: unknown) {
            customerLogger.warn('consent preferences sync failed', err);
            notifyConsentSyncFailed();
          }
        }

        if (isLoggedIn && loyaltyProfilingAvailable && sessionCustomerId !== null) {
          try {
            await syncLoyaltyProfilingConsent({
              api,
              browseTenantCode: tenantCode,
              customerId: sessionCustomerId,
              granted: next.profiling,
              memberships: platformMemberships.data?.memberships,
            });
          } catch (err: unknown) {
            customerLogger.warn('profiling consent sync failed', err);
            notifyConsentSyncFailed();
          }
        }

        const storageTenant = resolveConsentStorageTenantCode(tenantCode);
        publishCustomerConsentUpdated(storageTenant, { scope: 'global' });

        // G6 Spec A: end session on functional revoke when signed-in (empty access OK).
        if (!next.functional && isLoggedIn) {
          void endCustomerSessionGlobal({
            broadcast: true,
            callApi: true,
            api: 'logout',
            reason: 'consent',
          });
        }
      })();
    },
    [api, isLoggedIn, loyaltyProfilingAvailable, platformMemberships.data, sessionCustomerId, tenantCode]
  );

  const acceptAll = useCallback(() => {
    persist(
      buildAcceptAll(Date.now(), {
        loyaltyProfilingAvailable,
      })
    );
  }, [persist, loyaltyProfilingAvailable]);

  const acceptNecessary = useCallback(() => {
    persist(buildAcceptNecessary(Date.now()));
  }, [persist]);

  const acceptSelection = useCallback(
    (selection: {
      functional: boolean;
      analytics: boolean;
      marketing: boolean;
      profiling?: boolean;
    }) => {
      persist(buildAcceptSelection(selection, Date.now()));
    },
    [persist]
  );

  /** Remember-me only — must never close / dismiss the cookie banner. */
  const enableLoginPersistence = useCallback(() => {
    if (decision?.functional === true) {
      return;
    }
    const next = buildAcceptSelection(
      {
        functional: true,
        analytics: decision?.analytics ?? false,
        marketing: decision?.marketing ?? false,
        profiling: decision?.profiling ?? false,
      },
      Date.now()
    );
    writeConsent(tenantCode, next, undefined, { loyaltyProfilingAvailable });
    setDecision(next);
    // Keep whatever banner / prefer-center state is already open.
    if (bannerOpen) {
      manualBannerOpenRef.current = true;
    }
    publishCustomerConsentUpdated(resolveConsentStorageTenantCode(tenantCode), {
      scope: 'global',
    });
  }, [bannerOpen, decision, loyaltyProfilingAvailable, tenantCode]);

  const value = useMemo<CookieConsentContextValue>(() => {
    // Derived gate: while loyalty profiling availability is loading, treat CMP
    // as not ready so returning visitors never see a premature banner flash.
    const hydratedForUi = consentHydrated && !profilingLoading;
    return {
      decision,
      isConsentHydrated: hydratedForUi,
      // Prefer-center / edit-consent reopen: `force` or preselect keeps banner open
      // across storage rehydrate (manualBannerOpenRef).
      isBannerOpen: bannerOpen && hydratedForUi,
      bannerPreselectFunctional,
      openBanner: (opts) => {
        const preselect = opts?.preselectFunctional === true;
        const force = opts?.force === true || preselect;
        setBannerPreselectFunctional(preselect);
        setConsentHydrated(true);
        if (force) {
          manualBannerOpenRef.current = true;
        }
        setBannerOpen(true);
      },
      closeBanner: () => {
        manualBannerOpenRef.current = false;
        setBannerOpen(false);
        setBannerPreselectFunctional(false);
      },
      acceptAll,
      acceptNecessary,
      acceptSelection,
      enableLoginPersistence,
      loyaltyProfilingAvailable,
      profilingLoading,
      isLoggedIn,
      loginPersistenceAllowed: decision !== null && decision.functional,
      showRememberMeField:
        hydratedForUi && functionalAtHydration === false,
    };
  }, [
    bannerPreselectFunctional,
    decision,
    consentHydrated,
    bannerOpen,
    acceptAll,
    acceptNecessary,
    acceptSelection,
    enableLoginPersistence,
    loyaltyProfilingAvailable,
    profilingLoading,
    isLoggedIn,
    functionalAtHydration,
  ]);

  return (
    <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (ctx === null) {
    throw new Error(
      'useCookieConsent must be used inside <CookieConsentProvider>'
    );
  }
  return ctx;
}
